import Grid from "../grid";
import ModalAlerta from "../modal";
import { MdAdd } from "react-icons/md";
import ReactDOMServer from 'react-dom/server';
import { useLocation } from "react-router-dom";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { GridColDef, GridRowsProp, GridRenderCellParams } from "@mui/x-data-grid";
import { Form, Button, Dropdown, Tooltip, OverlayTrigger } from "react-bootstrap";
import { InputDataNativa } from "../input-data-nativa";
import { SubmenuAcaoLote } from "../listagem/submenu-acao-lote";
import { AcaoGrid, TipoLancamento, TipoStatusLancamento, StatusPagamento } from "../../services/tipos";
import { converterTipoLancamentoParaTexto, converterTipoStatusLancamentoParaTexto, dateToInputValue, inputValueToDate, diaInicialDepoisDoFinal } from "../../services/utilitarios";
import { ajustarPeriodoPorNovaDataFinal, ajustarPeriodoPorNovaDataInicial } from "../../services/periodo";
import { FaSearch, FaFilter, FaCalculator, FaMoneyBillWave, FaClock, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaTable, FaFileExcel, FaEllipsisV, FaCheckSquare, FaRegSquare, FaTimes, FaRegCircle, FaBalanceScale, FaChartLine } from "react-icons/fa";
import { toast } from "react-toastify";

// --- INTERFACES FILTROS ---

export interface FiltroDropdownOpcao {
    label: string;
    value: string | number | boolean;
}

export interface FiltroDropdownConfig {
    key: string;
    label: string;
    placeholder?: string;
    opcoes: FiltroDropdownOpcao[];
    value: string | number | boolean | (string | number | boolean)[];
    onChange: (valor: any) => void;
    multiSelect?: boolean;
}

export interface FiltroDataConfig {
    dataInicio: Date | null;
    dataFim: Date | null;
    onChangeDataInicio: (data: Date | null) => void;
    onChangeDataFim: (data: Date | null) => void;
    labelInicio?: string;
    labelFim?: string;
}

export interface ConfiguracaoFiltros {
    dropdowns?: FiltroDropdownConfig[];
    datas?: FiltroDataConfig;
    aoBuscar?: () => void;
    carregando?: boolean;
}

export interface AcaoEmLote {
    titulo: string;
    icone: React.ElementType;
    onClick: (dataInicio: Date | null, dataFim: Date | null, status: TipoStatusLancamento, itensSelecionados: (string | number)[]) => void;
    subAcoes?: AcaoEmLote[];
}

interface ListagemLancamentoProps {
    buscar: boolean;
    carregando: boolean;
    linhas: GridRowsProp;
    nomeEntidade: string;
    colunas: GridColDef[];
    nomePropriedade: string;
    acoesExtras?: AcaoGrid[];
    exibirAcoesDeCrud?: boolean;
    handleCriar: (Entidade: any) => void;
    /** Sem handler, a ação não aparece (usuário sem permissão). */
    handleEditar?: (Entidade: any) => void;
    handleExclusao?: (Entidade: any) => void;
    /** Esconde o botão Novo (usuário sem permissão de criar). */
    ocultarNovo?: boolean;
    handleVisualizacao: (Entidade: any) => void;
    handleSelecionarEntidade: (Entidade: any) => void;
    IconeEntidade: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    onBuscar: (dataInicio: Date | null, dataFim: Date | null, status: TipoStatusLancamento, tipo: TipoLancamento, tipoPeriodo?: "vencimento" | "pagamento") => void;
    filtros?: ConfiguracaoFiltros;
    acoesEmLote?: AcaoEmLote[];
    exportOptions?: {
        field: string;
        headerName: string;
        valueGetter?: (params: { row: any }) => any;
    }[];
    tituloExportacao?: string;
    /** Altura da linha em pixels pelo conteúdo (ver Grid.alturaDaLinha); sem isso vale a padrão. */
    alturaDaLinha?: (row: any) => number;
}

interface Totalizadores {
    entradas: {
        valor: number;
        aVencer: number;
        valorAVencer: number;
        emAtraso: number;
        valorEmAtraso: number;
        resolvidos: number;
        valorResolvidos: number;
    };
    saidas: {
        valor: number;
        aVencer: number;
        valorAVencer: number;
        emAtraso: number;
        valorEmAtraso: number;
        resolvidos: number;
        valorResolvidos: number;
    };
    saldo: number;
    saldoPrevisto: number;
}

// --- COMPONENTES FILTROS ---

const FilterDropdownToggle = React.forwardRef<HTMLButtonElement, { children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }>(
    ({ children, onClick }, ref) => (
        <button
            ref={ref}
            onClick={(e) => { e.preventDefault(); onClick(e); }}
            className="w-full text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-800 bg-white text-left flex justify-between items-center transition-shadow duration-200"
            style={{ height: "34px", padding: "0.2rem 0.5rem" }}
        >
            {children}
        </button>
    )
);

const CustomDropdownToggle = React.forwardRef<HTMLButtonElement, { children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }>(
    ({ children, onClick }, ref) => (
        <button
            ref={ref}
            onClick={(e) => { e.preventDefault(); onClick(e); }}
            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
            style={{ height: "38px" }}
        >
            {children}
        </button>
    )
);

interface DropdownContentProps {
    config: FiltroDropdownConfig;
    onClose: () => void;
}

const DropdownContent: React.FC<DropdownContentProps> = ({ config, onClose }) => {
    const [termoBusca, setTermoBusca] = useState("");
    const [opcoesFiltradas, setOpcoesFiltradas] = useState(config.opcoes);
    const [isFiltering, setIsFiltering] = useState(false);

    useEffect(() => {
        setIsFiltering(true);
        const timeout = setTimeout(() => {
            const lower = termoBusca.toLowerCase();
            setOpcoesFiltradas(
                config.opcoes.filter(op => op.label.toLowerCase().includes(lower))
            );
            setIsFiltering(false);
        }, 300);
        return () => clearTimeout(timeout);
    }, [termoBusca, config.opcoes]);

    const getCleanValues = useCallback(() => {
        const values = Array.isArray(config.value) ? [...config.value] : [];
        return values.filter(v => v !== "" && v !== null && v !== undefined);
    }, [config.value]);

    const isSelected = useCallback((val: string | number | boolean) => {
        if (config.multiSelect) {
            const cleanValues = getCleanValues();
            return cleanValues.includes(val);
        }
        return config.value === val;
    }, [config.value, config.multiSelect, getCleanValues]);

    const sortedOptions = useMemo(() => {
        return [...opcoesFiltradas].sort((a, b) => {
            const selectedA = isSelected(a.value);
            const selectedB = isSelected(b.value);

            if (selectedA && !selectedB) return -1;
            if (!selectedA && selectedB) return 1;
            return 0;
        });
    }, [opcoesFiltradas, isSelected]);

    const hasSelection = useMemo(() => {
        if (config.multiSelect) {
            const cleanValues = getCleanValues();
            return cleanValues.length > 0;
        }
        return config.value !== "" && config.value !== null && config.value !== undefined;
    }, [config.value, config.multiSelect, getCleanValues]);

    const handleSelect = (val: string | number | boolean) => {
        if (config.multiSelect) {
            const currentValues = getCleanValues();
            const index = currentValues.indexOf(val);

            let newValues;
            if (index >= 0) {
                newValues = currentValues.filter(v => v !== val);
            } else {
                newValues = [...currentValues, val];
            }
            config.onChange(newValues);
        } else {
            config.onChange(val);
            onClose();
        }
    };

    const handleClear = () => {
        if (config.multiSelect) {
            config.onChange([]);
        } else {
            config.onChange("");
        }
    };

    const toggleSelectAll = () => {
        if (!config.multiSelect) return;

        const currentValues = getCleanValues();
        const allFilteredValues = opcoesFiltradas.map(op => op.value);
        const allSelected = allFilteredValues.every(val => currentValues.includes(val));

        let newValues;
        if (allSelected) {
            newValues = currentValues.filter(val => !allFilteredValues.includes(val));
        } else {
            const missing = allFilteredValues.filter(val => !currentValues.includes(val));
            newValues = [...currentValues, ...missing];
        }
        config.onChange(newValues);
    };

    const getSelectAllStatus = () => {
        const cleanValues = getCleanValues();
        if (cleanValues.length === 0) return "none";
        if (opcoesFiltradas.length === 0) return "none";

        const allFilteredValues = opcoesFiltradas.map(op => op.value);
        const allSelected = allFilteredValues.every(val => cleanValues.includes(val));

        return allSelected ? "all" : "some";
    };

    return (
        <div className="flex flex-col w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-2 border-b border-gray-100 bg-white sticky top-0 z-20 rounded-t-md">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-blue-300 focus:shadow-outline-blue sm:text-sm transition duration-150 ease-in-out"
                        placeholder="Pesquisar..."
                        value={termoBusca}
                        onChange={(e) => setTermoBusca(e.target.value)}
                        autoFocus
                    />
                    {termoBusca && (
                        <div
                            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                            onClick={() => setTermoBusca("")}
                        >
                            <FaTimes className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white sticky top-[53px] z-10 border-b border-gray-100 shadow-sm">
                {hasSelection && (
                    <div
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-gray-600 bg-white"
                        onClick={handleClear}
                    >
                        <FaTimes className="w-3 h-3" />
                        <span className="text-sm font-medium">Limpar seleção</span>
                    </div>
                )}

                {config.multiSelect && opcoesFiltradas.length > 0 && !isFiltering && (
                    <div
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-[#1F467E]"
                        onClick={toggleSelectAll}
                    >
                        <div className="min-w-[24px]">
                            {getSelectAllStatus() === "all" ? <FaCheckSquare className="w-5 h-5" /> : <FaRegSquare className="text-gray-300 w-5 h-5" />}
                        </div>
                        <span className="text-sm font-medium">
                            {getSelectAllStatus() === "all" ? "Desmarcar Todos" : "Marcar Todos"}
                        </span>
                    </div>
                )}
            </div>

            <div className="overflow-y-auto max-h-60 bg-white">
                {isFiltering ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Filtrando...
                    </div>
                ) : sortedOptions.length > 0 ? (
                    sortedOptions.map((opcao) => {
                        const selected = isSelected(opcao.value);
                        return (
                            <div
                                key={String(opcao.value)}
                                className={`px-3 py-2 cursor-pointer flex items-start gap-2 text-sm transition-colors hover:bg-gray-100 ${selected ? 'bg-blue-50' : ''}`}
                                onClick={() => handleSelect(opcao.value)}
                            >
                                <div className="text-[#1F467E] min-w-[24px] mt-0.5 flex-shrink-0">
                                    {config.multiSelect ? (
                                        selected ? <FaCheckSquare className="w-5 h-5" /> : <FaRegSquare className="text-gray-300 w-5 h-5" />
                                    ) : (
                                        selected ? <FaCheckCircle className="w-5 h-5" /> : <FaRegCircle className="text-gray-300 w-5 h-5" />
                                    )}
                                </div>
                                <span className={`whitespace-normal break-words leading-tight ${selected ? 'font-medium text-[#1F467E]' : 'text-gray-700'}`}>
                                    {opcao.label}
                                </span>
                            </div>
                        );
                    })
                ) : (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        Nenhum resultado encontrado
                    </div>
                )}
            </div>
        </div>
    );
};

const getWeekRange = (currentDate: Date): { startOfWeek: Date; endOfWeek: Date } => {
    const dayOfWeek = currentDate.getDay();
    const startOfWeek = new Date(currentDate);
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(currentDate.getDate() + diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
};

const ListagemLancamento: React.FC<ListagemLancamentoProps> = ({
    buscar,
    carregando,
    linhas,
    nomeEntidade,
    colunas,
    nomePropriedade,
    acoesExtras,
    exibirAcoesDeCrud,
    handleCriar,
    handleEditar,
    handleExclusao,
    ocultarNovo = false,
    handleVisualizacao,
    IconeEntidade,
    onBuscar,
    filtros,
    acoesEmLote,
    exportOptions,
    tituloExportacao,
    alturaDaLinha,
}) => {
    const [showFilters, setShowFilters] = useState(true);
    const [showTotals, setShowTotals] = useState(false);
    const [dataInicio, setDataInicio] = useState<Date | null>(null);
    const [dataFim, setDataFim] = useState<Date | null>(null);
    const [tipoPeriodo, setTipoPeriodo] = useState<"vencimento" | "pagamento">("vencimento");
    const [status, setStatus] = useState<TipoStatusLancamento>(TipoStatusLancamento.Todos);
    const [tipo, setTipo] = useState<TipoLancamento>(TipoLancamento.Todos);
    const [mensagem, setMensagem] = useState<string[]>([]);
    const [showAlerta, setShowAlerta] = useState(false);
    const [entidadeSelecionada, setEntidadeSelecionada] = useState<any>(null);
    const [searchInput, setSearchInput] = useState<string>("");
    const [filteredLinhas, setFilteredLinhas] = useState<GridRowsProp>(linhas);
    const [initialLoad, setInitialLoad] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [abrirModalExportacao, setAbrirModalExportacao] = useState(false);
    const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);

    const [totalizadores, setTotalizadores] = useState<Totalizadores>({
        entradas: {
            valor: 0,
            aVencer: 0,
            valorAVencer: 0,
            emAtraso: 0,
            valorEmAtraso: 0,
            resolvidos: 0,
            valorResolvidos: 0
        },
        saidas: {
            valor: 0,
            aVencer: 0,
            valorAVencer: 0,
            emAtraso: 0,
            valorEmAtraso: 0,
            resolvidos: 0,
            valorResolvidos: 0
        },
        saldo: 0,
        saldoPrevisto: 0
    });

    const location = useLocation();

    const { startOfWeek, endOfWeek } = useMemo(() => getWeekRange(new Date()), []);

    // URL já processada por este efeito. Usado para (re)inicializar os filtros da tela SOMENTE
    // quando a URL muda de fato (montagem/navegação) e NÃO a cada toggle de `buscar` (recarga
    // após salvar/editar) — antes, toda edição resetava período/status/busca da tela.
    const ultimaUrlFiltrosRef = useRef<string | null>(null);

    useEffect(() => {
        if (ultimaUrlFiltrosRef.current === location.search) {
            // Recarregamento sem mudança de URL: preserva os filtros que o usuário ajustou.
            return;
        }
        ultimaUrlFiltrosRef.current = location.search;

        const searchParams = new URLSearchParams(location.search);

        const dataInicioParam = searchParams.get('dataInicio');
        const dataFimParam = searchParams.get('dataFim');
        const statusParam = searchParams.get('status');
        const tipoParam = searchParams.get('tipo');
        const serachInputParam = searchParams.get('search');

        if (!dataInicioParam && !dataFimParam) {
            setDataInicio(startOfWeek);
            setDataFim(endOfWeek);
        } else {
            if (dataInicioParam) {
                const date = new Date(dataInicioParam);
                if (!isNaN(date.getTime())) {
                    setDataInicio(date);
                }
            }

            if (dataFimParam) {
                const date = new Date(dataFimParam);
                if (!isNaN(date.getTime())) {
                    setDataFim(date);
                }
            }
        }

        if (statusParam) {
            const statusNum = parseInt(statusParam, 10);
            if (!isNaN(statusNum)) {
                setStatus(statusNum as TipoStatusLancamento);
            }
        }

        if (tipoParam) {
            const tipoNum = parseInt(tipoParam, 10);
            if (!isNaN(tipoNum)) {
                setTipo(tipoNum as TipoLancamento);
            }
        }

        if (serachInputParam) {
            setSearchInput(serachInputParam)
        }

        if (buscar && (dataInicioParam || dataFimParam || statusParam || tipoParam)) {
            const inicio = dataInicioParam ? new Date(dataInicioParam) : dataInicio;
            const fim = dataFimParam ? new Date(dataFimParam) : dataFim;
            const stat = statusParam ? parseInt(statusParam, 10) as TipoStatusLancamento : status;
            const tip = tipoParam ? parseInt(tipoParam, 10) as TipoLancamento : tipo;

            onBuscar(inicio, fim, stat, tip, tipoPeriodo);
        }
    }, [buscar, location.search, startOfWeek, endOfWeek, tipoPeriodo]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth > 768) setShowFilters(true);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (initialLoad && dataInicio && dataFim) {
            setIsSearching(true);
            onBuscar(dataInicio, dataFim, status, tipo, tipoPeriodo);
            setInitialLoad(false);
        }
    }, [dataInicio, dataFim, initialLoad, onBuscar, status, tipo, tipoPeriodo]);

    useEffect(() => {
        if (buscar && dataInicio && dataFim && !initialLoad) {
            setIsSearching(true);
            if (filtros?.aoBuscar) {
                 filtros.aoBuscar();
            } else {
                 onBuscar(dataInicio, dataFim, status, tipo, tipoPeriodo);
            }
        }
    }, [buscar, status, tipo, tipoPeriodo]);

    useEffect(() => {
        if (!carregando) {
            setIsSearching(false);
        }
    }, [carregando]);

    // Reseta filteredLinhas quando linhas muda, mas o useEffect de searchInput abaixo vai filtrar de novo se necessário
    useEffect(() => {
        setFilteredLinhas(linhas);
    }, [linhas]);

    const calcularTotalizadores = useCallback(() => {
        const hoje = new Date();

        // Totalizadores respeitam o periodo ativo da tela (mesma definicao do dashboard, que soma
        // por DataRealizado dentro de [dataInicio, dataFim]). Sem isso, "em atraso" incluia vencidos
        // de periodos anteriores presentes na lista e divergia do card do dashboard.
        const inicioPeriodo = dataInicio ? new Date(dataInicio.getFullYear(), dataInicio.getMonth(), dataInicio.getDate(), 0, 0, 0, 0) : null;
        const fimPeriodo = dataFim ? new Date(dataFim.getFullYear(), dataFim.getMonth(), dataFim.getDate(), 23, 59, 59, 999) : null;
        const dentroDoPeriodo = (linha: any) => {
            if (!inicioPeriodo || !fimPeriodo) return true;
            const d = new Date(linha.dataRealizado);
            return !isNaN(d.getTime()) && d >= inicioPeriodo && d <= fimPeriodo;
        };

        const entradas = filteredLinhas.filter(linha =>
            linha.tipo === TipoLancamento.Entrada && dentroDoPeriodo(linha)
        );

        const saidas = filteredLinhas.filter(linha =>
            linha.tipo === TipoLancamento.Saida && dentroDoPeriodo(linha)
        );

        const entradasAVencer = entradas.filter(linha =>
            linha.status === TipoStatusLancamento.Pendente &&
            new Date(linha.dataRealizado) >= hoje
        );
        const valorEntradasAVencer = entradasAVencer.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const entradasEmAtraso = entradas.filter(linha =>
            linha.status === TipoStatusLancamento.Pendente &&
            new Date(linha.dataRealizado) < hoje
        );
        const valorEntradasEmAtraso = entradasEmAtraso.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const entradasResolvidos = entradas.filter(linha =>
            linha.status === TipoStatusLancamento.Resolvido
        );
        const valorEntradasResolvidos = entradasResolvidos.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const saidasAVencer = saidas.filter(linha =>
            linha.status === TipoStatusLancamento.Pendente &&
            new Date(linha.dataRealizado) >= hoje
        );
        const valorSaidasAVencer = saidasAVencer.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const saidasEmAtraso = saidas.filter(linha =>
            linha.status === TipoStatusLancamento.Pendente &&
            new Date(linha.dataRealizado) < hoje
        );
        const valorSaidasEmAtraso = saidasEmAtraso.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const saidasResolvidos = saidas.filter(linha =>
            linha.status === TipoStatusLancamento.Resolvido
        );
        const valorSaidasResolvidos = saidasResolvidos.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const totalEntradas = entradas.reduce((sum, linha) => sum + (linha.valor || 0), 0);
        const totalSaidas = saidas.reduce((sum, linha) => sum + (linha.valor || 0), 0);

        const saldoReal = valorEntradasResolvidos - valorSaidasResolvidos;
        const saldoPrevisto = totalEntradas - totalSaidas;

        setTotalizadores({
            entradas: {
                valor: totalEntradas,
                aVencer: entradasAVencer.length,
                valorAVencer: valorEntradasAVencer,
                emAtraso: entradasEmAtraso.length,
                valorEmAtraso: valorEntradasEmAtraso,
                resolvidos: entradasResolvidos.length,
                valorResolvidos: valorEntradasResolvidos
            },
            saidas: {
                valor: totalSaidas,
                aVencer: saidasAVencer.length,
                valorAVencer: valorSaidasAVencer,
                emAtraso: saidasEmAtraso.length,
                valorEmAtraso: valorSaidasEmAtraso,
                resolvidos: saidasResolvidos.length,
                valorResolvidos: valorSaidasResolvidos
            },
            saldo: saldoReal,
            saldoPrevisto: saldoPrevisto
        });
    }, [filteredLinhas, dataInicio, dataFim]);

    useEffect(() => {
        calcularTotalizadores();
    }, [filteredLinhas, calcularTotalizadores]);

    const getFormattedValue = useCallback((coluna: GridColDef, linha: any): string => {
        if (coluna.renderCell) {
            const params: GridRenderCellParams = {
                id: linha.id,
                field: coluna.field,
                value: linha[coluna.field],
                row: linha,
                colDef: { ...coluna, computedWidth: 100, hasBeenResized: false },
                api: {} as any,
                hasFocus: false,
                tabIndex: -1,
                rowNode: {
                    id: linha.id,
                    depth: 0,
                    type: 'leaf',
                    parent: linha.id,
                    groupingKey: linha.id.toString(),
                },
                cellMode: 'view',
                isEditable: false,
                formattedValue: linha[coluna.field],
            };
            const renderizado = coluna.renderCell(params);
            return typeof renderizado === 'string'
                ? renderizado
                : React.isValidElement(renderizado)
                    ? ReactDOMServer.renderToStaticMarkup(renderizado).replace(/<[^>]+>/g, '')
                    : linha[coluna.field]?.toString() || "";
        }
        return linha[coluna.field]?.toString() || "";
    }, []);

    const filterData = useCallback((query: string) => {
        if (!query.trim()) {
            setFilteredLinhas(linhas);
            setIsSearching(false);
            return;
        }

        const lowerQuery = query.trim().toLowerCase();
        const filtered = linhas.filter((linha) =>
            colunas.some((coluna) => {
                const formattedValue = getFormattedValue(coluna, linha).toLowerCase();
                return formattedValue.includes(lowerQuery);
            })
        );
        setFilteredLinhas(filtered);
        setIsSearching(false);
    }, [linhas, colunas, getFormattedValue]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        if (searchInput.trim() === '') {
            filterData(searchInput);
            return;
        }

        setIsSearching(true);

        const timeout = setTimeout(() => {
            filterData(searchInput);
        }, 500);

        setSearchTimeout(timeout);

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [searchInput, filterData]);

    const handleBuscar = useCallback((dataIni: Date | null, dataFim: Date | null, stat: TipoStatusLancamento, tip: TipoLancamento) => {
        if ((dataIni && !dataFim) || (dataFim && !dataIni)) {
            toast.info('Informe a Data inicial e a Data Final');
            setIsSearching(false);
            return;
        } else if ((dataIni && dataFim) && diaInicialDepoisDoFinal(dataIni, dataFim)) {
            toast.info('A Data Final deve ser Maior ou Igual a data Inicial');
            setIsSearching(false);
            return;
        }

        setDataInicio(dataIni);
        setDataFim(dataFim);
        setStatus(stat);
        setTipo(tip);
        
        setIsSearching(true);
        
        if (filtros?.aoBuscar) {
             filtros.aoBuscar();
        } else {
             onBuscar(dataIni, dataFim, stat, tip, tipoPeriodo);
        }

        if (isMobile) setShowFilters(false);
    }, [onBuscar, isMobile, filtros, tipoPeriodo]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const handleHide = () => setShowAlerta(false);

    const handleExclusaoLocal = (linha: any) => {
        let entidadeIdentificador;

        if (nomePropriedade.includes(";")) {
            const propriedades = nomePropriedade.split(";");
            entidadeIdentificador = propriedades
                .map((prop) =>
                    prop.includes(".")
                        ? prop.split(".").reduce((obj, key) => obj?.[key], linha)
                        : linha[prop]
                )
                .join(" ");
        } else if (nomePropriedade.includes(".")) {
            const propriedades = nomePropriedade.split(".");
            entidadeIdentificador = propriedades.reduce((obj, prop) => obj?.[prop], linha);
        } else {
            entidadeIdentificador = linha[nomePropriedade];
        }

        setEntidadeSelecionada(linha);
        setMensagem([
            "Deseja confirmar a exclusão?",
            "",
            `${nomeEntidade}: ${entidadeIdentificador}`,
            "",
        ]);
        setShowAlerta(true);
    };

    const handleSim = () => {
        handleExclusao?.(entidadeSelecionada);
        setShowAlerta(false);
    };

    const handleStatusChange = useCallback((selected: string | null) => {
        if (selected !== null) {
            const statusSelecionado = parseInt(selected, 10);
            if (!isNaN(statusSelecionado)) {
                setStatus(statusSelecionado as TipoStatusLancamento);
            }
        }
    }, []);

    const handleTipoChange = useCallback((selected: string | null) => {
        if (selected !== null) {
            const tipoSelecionado = parseInt(selected, 10);
            if (!isNaN(tipoSelecionado)) {
                setTipo(tipoSelecionado as TipoLancamento);
            }
        }
    }, []);

    const toggleFilters = () => {
        setShowFilters(prev => !prev);
    };

    const toggleTotals = () => {
        setShowTotals(prev => !prev);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const renderDropdownLabel = (config: FiltroDropdownConfig) => {
        let selectedValues: any[] = [];
        
        if (Array.isArray(config.value)) {
            selectedValues = config.value;
        } else if (config.value !== null && config.value !== undefined && config.value !== "") {
            selectedValues = [config.value];
        }

        selectedValues = selectedValues.filter(v => v !== null && v !== undefined && v !== "");

        if (selectedValues.length === 0) {
            return <span className="text-gray-400">{config.placeholder || "Selecione..."}</span>;
        }

        if (selectedValues.length === 1) {
            const val = selectedValues[0];
            const opt = config.opcoes.find(o => o.value === val);
            return <span className="text-gray-800 truncate">{opt ? opt.label : val}</span>;
        }

        if (config.multiSelect && selectedValues.length === config.opcoes.length && config.opcoes.length > 0) {
             return <span className="text-gray-800">Todos</span>;
        }

        return <span className="text-gray-800">{selectedValues.length} selecionados</span>;
    };

    const saldoRealTooltip = (
        <Tooltip id="saldo-real-tooltip">
            <strong>Saldo Real</strong> - Considera apenas os lançamentos já resolvidos (pagamentos/recebimentos concluídos)
        </Tooltip>
    );

    const saldoPrevistoTooltip = (
        <Tooltip id="saldo-previsto-tooltip">
            <strong>Saldo Previsto</strong> - Considera todos os lançamentos, incluindo pendentes e futuros
        </Tooltip>
    );

    const ContadorComTooltip = ({
        quantidade,
        valor,
        icon: Icon,
        iconColor,
        texto
    }: {
        quantidade: number;
        valor: number;
        icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
        iconColor: string;
        texto: string;
    }) => {
        const tooltip = (
            <Tooltip id={`tooltip-${texto}`}>
                Valor total: {formatCurrency(valor)}
            </Tooltip>
        );

        return (
            <OverlayTrigger placement="top" overlay={tooltip}>
                <div className="flex items-center gap-1 cursor-help">
                    <Icon className={iconColor} />
                    <span>{quantidade} {texto}</span>
                </div>
            </OverlayTrigger>
        );
    };

    const limparParametrosUrl = () => {
        window.history.replaceState({}, '', window.location.pathname);
    };

    return (
        <div className="flex flex-col h-[calc(100vh-128px)] w-full bg-white-100 overflow-hidden">
            <div className="flex-1 flex flex-col bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-3 border-b border-gray-200">
                    <div className="flex items-center mb-1 sm:mb-0">
                        <IconeEntidade className="text-blue-800 w-5 h-5 mr-2" />
                        <span className="text-base font-semibold text-black">{nomeEntidade}</span>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                            onClick={() => setAbrirModalExportacao(true)}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                            style={{ height: "38px" }}
                        >
                            <div className="flex items-center gap-2">
                                <FaFileExcel className="w-3 h-3" />
                                <span className="font-medium">Exportar Dados</span>    
                            </div>
                        </button>

                        {acoesEmLote && acoesEmLote.length > 0 && (
                            <Dropdown onToggle={(isOpen) => setActionsDropdownOpen(isOpen)}>
                                <Dropdown.Toggle as={CustomDropdownToggle}>
                                    <div className="flex items-center gap-2">
                                        <FaEllipsisV className="w-3 h-3" />
                                        <span className="font-medium">Ações em Lote</span>
                                        <svg
                                            className={`w-3 h-3 transform transition-transform ${actionsDropdownOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {acoesEmLote.map((acao, index) => {
                                        const IconeAcao = acao.icone;
                                        // Repassa os ids das linhas exibidas (filtros e busca já aplicados) para a ação agir sobre elas
                                        const executar = (a: AcaoEmLote) => a.onClick(dataInicio, dataFim, status, filteredLinhas.map((linha: any) => linha.id));
                                        if (acao.subAcoes && acao.subAcoes.length > 0) {
                                            return <SubmenuAcaoLote key={index} acao={acao} executar={executar} />;
                                        }
                                        return (
                                            <Dropdown.Item
                                                key={index}
                                                onClick={() => executar(acao)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {IconeAcao && (
                                                        <span className="text-[#1F467E] flex items-center justify-center">
                                                            <IconeAcao className="w-4 h-4" />
                                                        </span>
                                                    )}
                                                    <span>{acao.titulo}</span>
                                                </div>
                                            </Dropdown.Item>
                                        );
                                    })}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}

                        <button
                            onClick={toggleTotals}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                            style={{ height: "38px" }}
                        >
                            <div className="flex items-center gap-2">
                                <FaCalculator className="w-3 h-3" />
                                <span className="font-medium">Totalizadores</span>
                                <svg
                                    className={`w-3 h-3 transform transition-transform ${showTotals ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                        <button
                            onClick={toggleFilters}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                            style={{ height: "38px" }}
                        >
                            <div className="flex items-center gap-2">
                                <FaFilter className="w-3 h-3" />
                                <span className="font-medium">Filtros</span>
                                <svg
                                    className={`w-3 h-3 transform transition-transform ${showFilters ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </button>
                        {!ocultarNovo && (
                        <Button
                            style={{
                                backgroundColor: "#1F467E",
                                color: "white",
                                height: "38px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            className="text-sm px-4 rounded border-0 hover:bg-[#17355F]"
                            onClick={handleCriar}
                            data-tour="btn-cadastrar"
                        >
                            Novo <MdAdd className="ml-2 w-4 h-4" />
                        </Button>
                        )}
                    </div>
                </div>

                {showTotals && (
                    <div className="px-3 py-2 bg-white border-b border-gray-200">
                        <div className="mb-2 text-xs text-gray-600 flex items-center gap-1">
                            <FaInfoCircle className="text-[#1F467E]" />
                            <span>Os totalizadores consideram os filtros aplicados.</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="bg-white rounded border border-gray-200 px-3 py-2" style={{ borderLeft: "4px solid #2F855A" }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Entradas</span>
                                    <FaMoneyBillWave className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <div className="text-lg font-semibold text-gray-900 leading-tight mt-1">{formatCurrency(totalizadores.entradas.valor)}</div>
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <ContadorComTooltip quantidade={totalizadores.entradas.aVencer} valor={totalizadores.entradas.valorAVencer} icon={FaClock} iconColor="text-gray-400" texto="a vencer" />
                                    <ContadorComTooltip quantidade={totalizadores.entradas.emAtraso} valor={totalizadores.entradas.valorEmAtraso} icon={FaExclamationTriangle} iconColor="text-gray-400" texto="atraso" />
                                    <ContadorComTooltip quantidade={totalizadores.entradas.resolvidos} valor={totalizadores.entradas.valorResolvidos} icon={FaCheckCircle} iconColor="text-gray-400" texto="resolvidos" />
                                </div>
                            </div>

                            <div className="bg-white rounded border border-gray-200 px-3 py-2" style={{ borderLeft: "4px solid #C53030" }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Saídas</span>
                                    <FaMoneyBillWave className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <div className="text-lg font-semibold text-gray-900 leading-tight mt-1">{formatCurrency(totalizadores.saidas.valor)}</div>
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <ContadorComTooltip quantidade={totalizadores.saidas.aVencer} valor={totalizadores.saidas.valorAVencer} icon={FaClock} iconColor="text-gray-400" texto="a vencer" />
                                    <ContadorComTooltip quantidade={totalizadores.saidas.emAtraso} valor={totalizadores.saidas.valorEmAtraso} icon={FaExclamationTriangle} iconColor="text-gray-400" texto="atraso" />
                                    <ContadorComTooltip quantidade={totalizadores.saidas.resolvidos} valor={totalizadores.saidas.valorResolvidos} icon={FaCheckCircle} iconColor="text-gray-400" texto="resolvidos" />
                                </div>
                            </div>

                            <div className="bg-white rounded border border-gray-200 px-3 py-2" style={{ borderLeft: `4px solid ${totalizadores.saldo >= 0 ? "#1F467E" : "#C53030"}` }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Saldo real</span>
                                    <FaBalanceScale className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <div className="text-lg font-semibold text-gray-900 leading-tight mt-1">{formatCurrency(totalizadores.saldo)}</div>
                                <div className="text-xs text-gray-500">{totalizadores.saldo >= 0 ? "Positivo" : "Negativo"}, considerando só o resolvido</div>
                            </div>

                            <div className="bg-white rounded border border-gray-200 px-3 py-2" style={{ borderLeft: `4px solid ${totalizadores.saldoPrevisto >= 0 ? "#1F467E" : "#C53030"}` }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Saldo previsto</span>
                                    <FaChartLine className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <div className="text-lg font-semibold text-gray-900 leading-tight mt-1">{formatCurrency(totalizadores.saldoPrevisto)}</div>
                                <div className="text-xs text-gray-500">{totalizadores.saldoPrevisto >= 0 ? "Positivo" : "Negativo"}, incluindo o que está em aberto</div>
                            </div>
                        </div>
                    </div>
                )}

                {showFilters && (
                    <div className="px-3 py-2 bg-white border-b border-gray-200">
                        {/* Grid uniforme para todos os filtros */}
                        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                            {/* Campo de Busca */}
                            <div className="flex-[1.4] min-w-[200px]">
                                <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Buscar</Form.Label>
                                <div className="relative">
                                    <Form.Control
                                        type="text"
                                        placeholder="Digite para filtrar..."
                                        value={searchInput}
                                        onChange={handleSearchChange}
                                        className="w-full text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800 pr-8"
                                        style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                    />
                                    {searchInput && (
                                        <div
                                            className="absolute inset-y-0 right-0 pr-2 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                                            onClick={() => setSearchInput("")}
                                        >
                                            <FaTimes />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Filtros Avançados (Dropdowns) */}
                            {filtros?.dropdowns?.map((dropdownConfig) => (
                                <div key={dropdownConfig.key} className="flex-1 min-w-[160px]">
                                    <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">{dropdownConfig.label}</Form.Label>
                                    <Dropdown
                                        className="w-full"
                                        show={openDropdownKey === dropdownConfig.key}
                                        onToggle={(isOpen) => setOpenDropdownKey(isOpen ? dropdownConfig.key : null)}
                                    >
                                        <Dropdown.Toggle as={FilterDropdownToggle}>
                                            <div className="flex-1 overflow-hidden">
                                                {renderDropdownLabel(dropdownConfig)}
                                            </div>
                                            <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-full p-0 shadow-lg border-0 rounded-md overflow-hidden">
                                            <DropdownContent
                                                config={dropdownConfig}
                                                onClose={() => setOpenDropdownKey(null)}
                                            />
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            ))}

                            {/* Período (datas agrupadas) + seletor de tipo */}
                            {filtros?.datas ? (
                                <div className="flex-[1.4] min-w-[340px] max-w-[460px]">
                                    <div className="flex items-center justify-between mb-1">
                                        <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-0">Período</Form.Label>
                                        <div className="flex rounded overflow-hidden border border-gray-300">
                                            <button
                                                type="button"
                                                onClick={() => setTipoPeriodo("vencimento")}
                                                className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${tipoPeriodo === "vencimento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
                                            >
                                                Vencimento
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setTipoPeriodo("pagamento")}
                                                className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors border-l border-gray-300 ${tipoPeriodo === "pagamento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
                                            >
                                                Pagamento
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <InputDataNativa
                                            value={dateToInputValue(filtros.datas.dataInicio)}
                                            onChange={(valor) => {
                                                const periodo = ajustarPeriodoPorNovaDataInicial(inputValueToDate(valor), filtros.datas?.dataFim ?? null);
                                                filtros.datas?.onChangeDataInicio(periodo.dataInicio);
                                                if (periodo.dataFim !== (filtros.datas?.dataFim ?? null)) {
                                                    filtros.datas?.onChangeDataFim(periodo.dataFim);
                                                }
                                            }}
                                            className="form-control flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800"
                                            style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                        />
                                        <span className="text-xs text-gray-500">até</span>
                                        <InputDataNativa
                                            value={dateToInputValue(filtros.datas.dataFim)}
                                            onChange={(valor) => {
                                                const periodo = ajustarPeriodoPorNovaDataFinal(filtros.datas?.dataInicio ?? null, inputValueToDate(valor));
                                                filtros.datas?.onChangeDataFim(periodo.dataFim);
                                                if (periodo.dataInicio !== (filtros.datas?.dataInicio ?? null)) {
                                                    filtros.datas?.onChangeDataInicio(periodo.dataInicio);
                                                }
                                            }}
                                            className="form-control flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800"
                                            style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-[1.4] min-w-[340px] max-w-[460px]">
                                    <div className="flex items-center justify-between mb-1">
                                        <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-0">Período</Form.Label>
                                        <div className="flex rounded overflow-hidden border border-gray-300">
                                            <button
                                                type="button"
                                                onClick={() => setTipoPeriodo("vencimento")}
                                                className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${tipoPeriodo === "vencimento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
                                            >
                                                Vencimento
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setTipoPeriodo("pagamento")}
                                                className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors border-l border-gray-300 ${tipoPeriodo === "pagamento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
                                            >
                                                Pagamento
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <InputDataNativa
                                            value={dateToInputValue(dataInicio)}
                                            onChange={(valor) => {
                                                const periodo = ajustarPeriodoPorNovaDataInicial(inputValueToDate(valor), dataFim);
                                                setDataInicio(periodo.dataInicio);
                                                setDataFim(periodo.dataFim);
                                            }}
                                            className="form-control flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800"
                                            style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                        />
                                        <span className="text-xs text-gray-500">até</span>
                                        <InputDataNativa
                                            value={dateToInputValue(dataFim)}
                                            onChange={(valor) => {
                                                const periodo = ajustarPeriodoPorNovaDataFinal(dataInicio, inputValueToDate(valor));
                                                setDataInicio(periodo.dataInicio);
                                                setDataFim(periodo.dataFim);
                                            }}
                                            className="form-control flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800"
                                            style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Status */}
                            <div className="flex-1 min-w-[150px]">
                                <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Status</Form.Label>
                                <Dropdown onSelect={handleStatusChange}>
                                    <Dropdown.Toggle as={FilterDropdownToggle}>
                                        <span className="flex-1 text-left">{converterTipoStatusLancamentoParaTexto(status)}</span>
                                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-full">
                                        {Object.values(TipoStatusLancamento).map(
                                            (statusValue) =>
                                                typeof statusValue === "number" && (
                                                    <Dropdown.Item
                                                        key={statusValue}
                                                        eventKey={statusValue.toString()}
                                                    >
                                                        {converterTipoStatusLancamentoParaTexto(statusValue)}
                                                    </Dropdown.Item>
                                                )
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            {/* Tipo */}
                            <div className="flex-1 min-w-[150px]">
                                <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Tipo</Form.Label>
                                <Dropdown onSelect={handleTipoChange}>
                                    <Dropdown.Toggle as={FilterDropdownToggle}>
                                        <span className="flex-1 text-left">{converterTipoLancamentoParaTexto(tipo)}</span>
                                        <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-full">
                                        {Object.values(TipoLancamento).map(
                                            (tipoValue) =>
                                                typeof tipoValue === "number" && (
                                                    <Dropdown.Item
                                                        key={tipoValue}
                                                        eventKey={tipoValue.toString()}
                                                    >
                                                        {converterTipoLancamentoParaTexto(tipoValue)}
                                                    </Dropdown.Item>
                                                )
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            <div>
                            <Button
                                style={{
                                    backgroundColor: "#1F467E",
                                    color: "white",
                                    height: "34px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                className="text-sm px-4 rounded border-0 hover:bg-[#17355F]"
                                onClick={() => {
                                    limparParametrosUrl();
                                    handleBuscar(dataInicio, dataFim, status, tipo);
                                }}
                                disabled={isSearching}
                            >
                                {isSearching ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Buscando...
                                    </span>
                                ) : (
                                    <>
                                        Buscar <FaSearch className="ml-2 w-4 h-4" />
                                    </>
                                )}
                            </Button>
                        </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 p-2 overflow-auto">
                    <Grid
                        tudoMaiusculo={true}
                        linhas={filteredLinhas}
                        colunas={colunas}
                        carregando={carregando || isSearching}
                        acoesExtras={acoesExtras}
                        handleExclusao={handleExclusao ? handleExclusaoLocal : undefined}
                        handleEditar={handleEditar}
                        handleVisualizacao={handleVisualizacao}
                        exibirAcoesDeCrud={exibirAcoesDeCrud}
                        modalExportacaoAberto={abrirModalExportacao}
                        abrirModalExportacao={() => setAbrirModalExportacao(true)}
                        fecharModalExportacao={() => setAbrirModalExportacao(false)}
                        exportOptions={exportOptions}
                        tituloExportacao={tituloExportacao || nomeEntidade}
                        alturaDaLinha={alturaDaLinha}
                    />
                </div>

                <ModalAlerta
                    show={showAlerta}
                    ehDeSimNao={true}
                    onSim={handleSim}
                    message={mensagem}
                    onHide={handleHide}
                />
            </div>
        </div>
    );
};

export default ListagemLancamento;